import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress, Dialog, Tooltip as MuiTooltip } from "@mui/material";
import { Icon } from "@iconify/react";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import { CTADropdown } from "../../components/shared/Dropdown";
import BCHeader from "../../shared/BCHeader";
import GenericTableHeader from "../../shared/GenericTableHeader";
import ReportsAgGridTable from "./ReportsAgGridTable";
import { CustomResponse, ReportDto } from "../../types/Grant";
import { HissaReportType } from "../../types/common";
import {
  CustomReportSummary,
  listCustomReports,
  deleteCustomReport,
} from "../../api/customReportApi";
import AlertDialog from "../../components/shared/AlertDialog";
import {
  downloadPoolStatement,
  generateAcceptanceReport,
  generateCancellationReport,
  generateCashoutReport,
  generateEmployeeDetailsReport,
  generateExerciseReport,
  generateFinancialReport,
  generateExpenseReport,
  generateGrantReport,
  generateLiquidityReport,
  generateSebiReport,
  generateTenCReport,
  generateTransactionReport,
  generateVestWiseReport,
} from "../../api/Esop";
import { useCompanyStore } from "../../store";
import { useReportJobStore } from "../../store/reportJobStore";
import { formatDisplayDate } from "../../utils/date";
import { downloadS3File } from "../../utils/DownloadFile";
import { useIsInternalUser } from "../../utils/useIsInternalUser";
import { useGetHistoricReports } from "../../queries";
import { useGetScheduledReports } from "../../queries/scheduledReports";
import { useGetReportSettings } from "../../queries/siteSettings";
import { CustomReportGenerateDialog } from "./dialogs/CustomReportGenerateDialog";
import ScheduleManagementDialog from "./dialogs/ScheduleManagementDialog";

export interface ReportNames {
  name: string;
  description: string;
  type: string;
  headerName: string;
  startDate: string;
  endDate: string;
  reportType?: HissaReportType;
  onSubmitFunction?: (param: any) => Promise<CustomResponse<ReportDto>>;
}

const reportNames: ReportNames[] = [
  {
    name: "Grant Report",
    description: "Provides details of all grants issued under the ESOP plan.",
    type: "Grant",
    headerName: "Download Grant Report",
    reportType: HissaReportType.GRANT_REPORT,
    onSubmitFunction: generateGrantReport,
    startDate: "Grant Start Date",
    endDate: "Grant End Date",
  },
  {
    name: "Vest-wise Report",
    description: "Provides details of all grants issued under the ESOP plan.",
    type: "Vesting",
    headerName: "Download Vestwise Report",
    reportType: HissaReportType.VEST_WISE_REPORT,
    onSubmitFunction: generateVestWiseReport,
    startDate: "Grant Start Date",
    endDate: "Grant End Date",
  },
  {
    name: "Cancellation Report",
    description: "Tracks the cancellation of any ESOP grants or shares.",
    type: "Transaction",
    headerName: "Download Cancellation Report",
    reportType: HissaReportType.CANCELLATION_REPORT,
    onSubmitFunction: generateCancellationReport,
    startDate: "Transaction Start Date",
    endDate: "Transaction End Date",
  },
  {
    name: "Employee wise Report",
    description: "Offers a breakdown of ESOP details by employee.",
    type: "Employee",
    reportType: HissaReportType.EMPLOYEE_WISE_REPORT,
    headerName: "Download Employee wise Report",
    onSubmitFunction: generateEmployeeDetailsReport,
    startDate: "Transaction Start Date",
    endDate: "Transaction End Date",
  },
  {
    name: "Exercise Report",
    description: "Summarizes the exercise of vested options by employees.",
    type: "Transaction",
    reportType: HissaReportType.EXERCISE_REPORT,
    headerName: "Download Exercise Report",
    onSubmitFunction: generateExerciseReport,
    startDate: "Transaction Start Date",
    endDate: "Transaction End Date",
  },
  {
    name: "Cashout Report",
    description: "Summarizes the cashout of vested options by employees.",
    type: "Transaction",
    reportType: HissaReportType.CASHOUT_REPORT,
    headerName: "Download Cashout Report",
    onSubmitFunction: generateCashoutReport,
    startDate: "Transaction Start Date",
    endDate: "Transaction End Date",
  },
  {
    name: "Liquidity Report",
    description: "Displays liquidity events and associated stock movements.",
    type: "Grant",
    headerName: "Download Liquidity Report",
    reportType: HissaReportType.LIQUIDITY_REPORT,
    onSubmitFunction: generateLiquidityReport,
    startDate: "Event Start Date",
    endDate: "Event End Date",
  },
  {
    name: "BuyBack Report",
    description: "Details any buyback events of employee shares.",
    type: "Grant",
    reportType: HissaReportType.BUYBACK_REPORT,
    headerName: "Download BuyBack Report",
    onSubmitFunction: generateLiquidityReport,
    startDate: "Event Start Date",
    endDate: "Event End Date",
  },
  {
    name: "Pool Statement",
    description: "Shows the available and used ESOP pool balance.",
    type: "Company",
    reportType: HissaReportType.POOL_STATEMENT,
    headerName: "Download Pool Statement",
    onSubmitFunction: downloadPoolStatement,
    startDate: "",
    endDate: "",
  },
  {
    name: "Acceptance Report",
    description: "Documents the acceptance of ESOP offers by employees.",
    type: "Grant",
    reportType: HissaReportType.ACCEPTANCE_REPORT,
    headerName: "Download Acceptance Report",
    onSubmitFunction: generateAcceptanceReport,
    startDate: "Grant Start Date",
    endDate: "Grant End Date",
  },
  {
    name: "Financial Report",
    description: "Provides financial implications and status of the ESOP plan.",
    type: "Grant",
    reportType: HissaReportType.FINANCIAL_REPORT,
    headerName: "Download Financial Report",
    onSubmitFunction: generateFinancialReport,
    startDate: "Grant Start Date",
    endDate: "Grant End Date",
  },
  {
    name: "SH-6 Report",
    description: "Regulatory report for managing ESOP compliance.",
    type: "Grant",
    reportType: HissaReportType.SH6_REPORT,
    headerName: "Download SH-6 Report",
    startDate: "",
    endDate: "",
  },
  {
    name: "Employee Data Sync history",
    description:
      "Comprehensive Report on Employee Data Synchronization History from External Data Sources",
    type: "Employee",
    headerName: "Download Sync History Report",
    startDate: "",
    endDate: "",
  },
  {
    name: "ESOP Accounting Report",
    description: "Compensation Report For Company",
    type: "Grant",
    reportType: HissaReportType.ESOP_ACCOUNTING,
    headerName: "Download ESOP Accounting Report",
    startDate: "",
    endDate: "",
  },
  {
    name: "Section 10-C Report",
    description: "Section 10-C Report for ESOP Exercise",
    type: "Grant",
    reportType: HissaReportType.SECTION_10_C_REPORT,
    headerName: "Download Section 10-C Report",
    onSubmitFunction: generateTenCReport,
    startDate: "From Date",
    endDate: "To Date",
  },
  {
    name: "Expense Report",
    description:
      "Ind AS 102 ESOP expense report covering fair value, amortisation, and diluted EPS disclosure.",
    type: "Grant",
    reportType: HissaReportType.EXPENSE_REPORT,
    headerName: "Download Expense Report",
    onSubmitFunction: generateExpenseReport,
    startDate: "Period Start Date",
    endDate: "Period End Date",
  },
];

const Reports = () => {
  const navigate = useNavigate();
  const { companyData } = useCompanyStore();
  const [customReports, setCustomReports] = useState<CustomReportSummary[]>([]);
  const [expandedCustom, setExpandedCustom] = useState<Set<string>>(new Set());
  const [generateDialog, setGenerateDialog] =
    useState<CustomReportSummary | null>(null);
  const [overflowDialog, setOverflowDialog] = useState<{
    mode: "recent" | "schedule";
    report: CustomReportSummary;
  } | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    report: CustomReportSummary;
    loading: boolean;
  } | null>(null);
  const isInternalUser = useIsInternalUser();

  const { data: historicReports = [], refetch: refetchHistoric } =
    useGetHistoricReports();
  const { data: scheduledReports = [], refetch: refetchScheduled } =
    useGetScheduledReports();
  const { data: reportSettings } = useGetReportSettings();
  const { reports: jobReports } = useReportJobStore();
  const companyJobReports = jobReports[companyData?.id ?? ""] ?? {};

  // CUSTOM_REPORT history/schedule/job-status are scoped per-customReportId.
  // BE returns one historic entry per saved custom report (tagged with
  // customReportId in reportMetaData); schedules carry customReportId in
  // params; and the reportJobStore is keyed by `${reportType}:${customReportId}`
  // for CUSTOM_REPORT slots. All three lookups happen inside the map() below.

  useEffect(() => {
    listCustomReports()
      .then((data) => setCustomReports(data ?? []))
      .catch(() => {});
  }, []);
  if (companyData?.companyMetaData.typeOfTheCompany === "PUBLIC") {
    if (!reportNames.find((report) => report.name === "SEBI Report")) {
      reportNames.push({
        name: "SEBI Report",
        description: "SEBI Report for ESOP Exercise",
        type: "Plan",
        reportType: HissaReportType.SEBI_REPORT,
        headerName: "Download SEBI Report",
        startDate: "From Date",
        endDate: "To Date",
        onSubmitFunction: generateSebiReport,
      });
    }
  }
  return (
    <VStack className="gap-4">
      <HStack className="justify-between min-h-[45px] items-center">
        <BCHeader
          className="items-baseline "
          bcTitle="Reports"
          bcSubTitle={""}
        />
        {isInternalUser && (
          <ButtonPrimary onClick={() => navigate("/options/reports/builder")}>
            + Custom Report Builder
          </ButtonPrimary>
        )}
      </HStack>

      {reportSettings?.excelPasswordProtectionEnabled === false && (
        <Box className="w-full border border-amber-200 bg-amber-50 rounded-lg p-4 hidden">
          <HStack className="w-full items-center justify-between gap-4">
            <VStack className="min-w-0 gap-1">
              <p className="text-sm font-semibold text-headerColor">
                Excel reports are not password-protected
              </p>
              <p className="text-xs text-secondaryText">
                Enable protection to encrypt each Excel export with the report
                requester&apos;s display name and your company CIN.
              </p>
            </VStack>
            <ButtonPrimary1
              type="button"
              onClick={() =>
                navigate("/settings/siteSettings", {
                  state: { activeSetting: "Report Settings" },
                })
              }
            >
              Enable protection
            </ButtonPrimary1>
          </HStack>
        </Box>
      )}

      {/* Standard Reports */}
      <Box className="p-4 bg-white border rounded-lg min-w-min border-borderColor drop-shadow-box">
        <HStack className="justify-between w-full">
          <ReportsAgGridTable reportsData={reportNames} />
        </HStack>
      </Box>

      {/* Team-visible reports can be generated and downloaded by company users. */}
      {customReports.length > 0 && (
        <Box className="p-4 bg-white border rounded-lg border-borderColor drop-shadow-box">
          <VStack className="w-full gap-0">
            <p className="text-sm font-semibold text-headerColor mb-3">
              Custom Reports
              <span className="ml-2 text-xs font-normal text-secondaryText">
                {customReports.length} report
                {customReports.length !== 1 ? "s" : ""}
              </span>
            </p>
            <table className="table-auto w-full border-separate border-spacing-y-3">
              <thead className="sr-only">
                <tr>
                  <th>Report</th>
                  <th>Last updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customReports.map((report) => {
                  const isExpanded = expandedCustom.has(report.id);
                  const allColumns = report.sheets.flatMap((s) => s.columns);
                  const uniqueColumns = [...new Set(allColumns)];

                  // Per-report lookups — scoped by customReportId (= report.id),
                  // not by the shared reportType=CUSTOM_REPORT tag.
                  const historic = historicReports.find(
                    (r: any) =>
                      r.reportType === "CUSTOM_REPORT" &&
                      r.customReportId === report.id
                  );
                  const downloadPath = historic?.path ?? null;
                  const schedule = scheduledReports.find(
                    (s: any) =>
                      s.reportType === "CUSTOM_REPORT" &&
                      (s.params as any)?.customReportId === report.id &&
                      s.isActive
                  );
                  const jobStatus =
                    (companyJobReports as any)[`CUSTOM_REPORT:${report.id}`]
                      ?.status ?? null;

                  const isProcessing =
                    jobStatus === "IN_PROGRESS" || jobStatus === "QUEUED";
                  const lastGeneratedText = isProcessing
                    ? "Processing..."
                    : historic?.createdAt
                    ? `Last generated on ${formatDisplayDate(
                        new Date(historic.createdAt)
                      )}`
                    : "Never generated";

                  return (
                    <tr
                      key={report.id}
                      className="w-full bg-white shadow-sm border border-gray-100 rounded-lg"
                    >
                      <td className="align-top w-1/2 p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-headerColor">
                            {report.name}
                          </span>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium capitalize ${
                              report.visibility === "private"
                                ? "bg-tertiaryBg text-secondaryText"
                                : report.visibility === "team"
                                ? "bg-blue-50 text-blue-600"
                                : "bg-green-50 text-green-600"
                            }`}
                          >
                            {report.visibility}
                          </span>
                          {schedule && (
                            <span
                              title="Has active schedule"
                              className="inline-flex items-center gap-1 text-xxs font-semibold text-orange-600 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5"
                            >
                              <Icon icon="mdi:clock-outline" width={10} />
                              Scheduled
                            </span>
                          )}
                        </div>

                        {report.description && (
                          <p className="text-xs text-secondaryText mt-1 leading-relaxed">
                            {report.description}
                          </p>
                        )}

                        {/* Sheet summary */}
                        {report.sheets.length > 0 && (
                          <>
                            <button
                              onClick={() =>
                                setExpandedCustom((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(report.id))
                                    next.delete(report.id);
                                  else next.add(report.id);
                                  return next;
                                })
                              }
                              className="mt-2 flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                            >
                              <Icon
                                icon={
                                  isExpanded
                                    ? "mdi:table-column-remove"
                                    : "mdi:table-column"
                                }
                                width={13}
                              />
                              {isExpanded
                                ? "Hide columns"
                                : `${report.sheets.length} sheet${
                                    report.sheets.length !== 1 ? "s" : ""
                                  }, ${uniqueColumns.length} column${
                                    uniqueColumns.length !== 1 ? "s" : ""
                                  }`}
                            </button>

                            {isExpanded && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {uniqueColumns.map((col) => (
                                  <span
                                    key={col}
                                    className="inline-block text-[10px] font-medium text-primaryText bg-tertiaryBg border border-borderColor rounded px-2 py-0.5 leading-snug cursor-default"
                                  >
                                    {col}
                                  </span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </td>

                      <td className="align-top w-1/5 p-4">
                        <div className="text-sm text-gray-700">
                          {lastGeneratedText}
                        </div>
                        {schedule?.nextRunAt && (
                          <div className="flex items-center gap-1 text-xs text-orange-600 mt-1">
                            <Icon
                              icon="mdi:clock-outline"
                              width={12}
                              className="shrink-0"
                            />
                            <span>
                              Next run:{" "}
                              {formatDisplayDate(new Date(schedule.nextRunAt))}
                            </span>
                          </div>
                        )}
                      </td>

                      <td className="align-top w-1/6 p-4">
                        <div className="flex justify-center">
                          {jobStatus ? (
                            <span
                              className={`py-1 px-3 rounded text-xs flex items-center gap-2 justify-center flex-wrap h-8 w-24 ${
                                jobStatus === "SUCCESS"
                                  ? "bg-green-100 text-green-700"
                                  : jobStatus === "IN_PROGRESS"
                                  ? "bg-blue-100 text-blue-700"
                                  : jobStatus === "FAILURE"
                                  ? "bg-red-100 text-red-700"
                                  : jobStatus === "QUEUED"
                                  ? "bg-gray-100 text-gray-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              <span>
                                {jobStatus === "SUCCESS"
                                  ? "Success"
                                  : jobStatus === "IN_PROGRESS"
                                  ? "In Progress"
                                  : jobStatus === "FAILURE"
                                  ? "Failed"
                                  : jobStatus === "QUEUED"
                                  ? "Queued"
                                  : "-"}
                              </span>
                            </span>
                          ) : (
                            <span className="py-1 px-3 rounded text-xs flex items-center gap-2 justify-center flex-wrap h-8 w-24 bg-gray-100 text-gray-700">
                              <span>-</span>
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="align-top p-4">
                        <div className="flex items-start gap-3 justify-end">
                          <div className="flex flex-col items-end gap-1">
                            <ButtonPrimary1
                              onClick={() => setGenerateDialog(report)}
                              disabled={isProcessing}
                              className="px-4 py-2 rounded-md font-medium flex items-center gap-1.5"
                            >
                              {isProcessing ? (
                                <>
                                  <CircularProgress
                                    size={12}
                                    className="text-primary"
                                  />
                                  Generating…
                                </>
                              ) : (
                                <>
                                  <Icon
                                    icon="mdi:cog-play-outline"
                                    width={15}
                                  />
                                  Generate Report
                                </>
                              )}
                            </ButtonPrimary1>
                            {isProcessing && (
                              <span className="text-[10px] text-gray-400 text-right">
                                Report is being prepared — come back to download
                              </span>
                            )}
                          </div>

                          <MuiTooltip
                            title={
                              downloadPath
                                ? `Download the last generated report${
                                    historic?.createdAt
                                      ? ` (${formatDisplayDate(
                                          historic.createdAt
                                        )})`
                                      : ""
                                  }`
                                : 'No report generated yet. Click "Generate Report" first and come back once it\'s ready.'
                            }
                            placement="top"
                            arrow
                          >
                            <span>
                              <button
                                disabled={!downloadPath}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!downloadPath) return;
                                  const fileName = `${
                                    companyData?.name || ""
                                  }-${report.name}-${formatDisplayDate(
                                    historic?.createdAt
                                  )}.xlsx`;
                                  downloadS3File(downloadPath, fileName, false);
                                }}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
                                  downloadPath
                                    ? "border-primary text-primary hover:bg-primary/10 cursor-pointer"
                                    : "border-gray-200 text-gray-300 cursor-not-allowed"
                                }`}
                              >
                                <Icon
                                  icon="material-symbols:download"
                                  width={16}
                                />
                                Download
                              </button>
                            </span>
                          </MuiTooltip>

                          <CTADropdown
                            className="items-center bg-white align-middle"
                            dropdownClassName=" xl:right-10 lg:right-8 md:right-2 bg-white z-10"
                            actions={[
                              { name: "View recent reports" },
                              ...(report.canEdit
                                ? [
                                    { name: "Manage schedule" },
                                    { name: "Edit report" },
                                    { name: "Delete report" },
                                  ]
                                : []),
                            ]}
                            onAction={(action) => {
                              if (action.name === "Manage schedule") {
                                setOverflowDialog({ mode: "schedule", report });
                              } else if (
                                action.name === "View recent reports"
                              ) {
                                setOverflowDialog({ mode: "recent", report });
                              } else if (action.name === "Delete report") {
                                setDeleteDialog({ report, loading: false });
                              } else {
                                navigate(
                                  `/options/reports/builder/${report.id}`
                                );
                              }
                            }}
                            render={() => (
                              <Icon
                                icon="carbon:overflow-menu-horizontal"
                                className="text-gray-400 rounded hover:bg-gray-300"
                                height={20}
                              />
                            )}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </VStack>
        </Box>
      )}

      {/* Generate dialog */}
      <Dialog
        open={generateDialog !== null}
        onClose={() => setGenerateDialog(null)}
        maxWidth="sm"
      >
        {generateDialog && (
          <CustomReportGenerateDialog
            report={generateDialog}
            onClose={() => setGenerateDialog(null)}
          />
        )}
      </Dialog>

      {/* Manage schedule dialog */}
      <Dialog
        open={overflowDialog?.mode === "schedule"}
        onClose={() => {
          setOverflowDialog(null);
          refetchScheduled();
        }}
        maxWidth="lg"
      >
        <div className="w-[600px] mx-auto bg-white rounded-lg">
          <ScheduleManagementDialog
            reportType={HissaReportType.CUSTOM_REPORT}
            customReportId={overflowDialog?.report.id}
            reportName={overflowDialog?.report.name ?? "Custom Report"}
            onClose={() => {
              setOverflowDialog(null);
              refetchScheduled();
            }}
          />
        </div>
      </Dialog>

      {/* Recent reports dialog */}
      <Dialog
        open={overflowDialog?.mode === "recent"}
        onClose={() => {
          setOverflowDialog(null);
          refetchHistoric();
        }}
        maxWidth="md"
      >
        <div className="w-[680px] mx-auto bg-white rounded-lg">
          <div className="flex items-center justify-between px-6 text-lg font-medium border-b py-5">
            <div>
              Recent Reports for{" "}
              {overflowDialog?.report.name ?? "Custom Report"}
            </div>
            <div
              onClick={() => setOverflowDialog(null)}
              className="cursor-pointer text-gray-500"
            >
              X
            </div>
          </div>
          <VStack className="p-6 gap-y-3 overflow-y-auto max-h-[300px]">
            {(() => {
              const activeReport = overflowDialog?.report;
              const activeHistoric = activeReport
                ? historicReports.find(
                    (r: any) =>
                      r.reportType === "CUSTOM_REPORT" &&
                      r.customReportId === activeReport.id
                  )
                : undefined;
              const prevReports = activeHistoric?.previousReports ?? [];
              if (prevReports.length === 0) {
                return (
                  <div className="text-sm text-gray-600">
                    No recent reports found. Generate a report first.
                  </div>
                );
              }
              return [...prevReports]
                .sort(
                  (a: any, b: any) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                )
                .map((h: any, idx: number) => {
                  const date = new Date(h.createdAt);
                  const time = date.toLocaleTimeString([], {
                    hour12: true,
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between w-full p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center shadow-sm">
                          <Icon
                            icon="ic:round-description"
                            className="text-primary"
                            width={20}
                          />
                        </div>
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-800">
                            {formatDisplayDate(date)}
                          </div>
                          <div className="text-xxs text-gray-500">
                            Generated at {time}
                          </div>
                        </div>
                      </div>
                      {h.path ? (
                        <button
                          onClick={() =>
                            downloadS3File(
                              h.path,
                              `${
                                overflowDialog?.report.name ?? "report"
                              }-${formatDisplayDate(h.createdAt)}.xlsx`,
                              false
                            )
                          }
                          className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-primary text-primary text-sm font-medium hover:bg-primary/10 transition-colors"
                        >
                          <Icon icon="material-symbols:download" width={15} />
                          Download
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400">
                          Not available
                        </span>
                      )}
                    </div>
                  );
                });
            })()}
          </VStack>
        </div>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteDialog !== null}
        onClose={() => setDeleteDialog(null)}
        message={`Are you sure you want to delete "${deleteDialog?.report.name}"? This cannot be undone.`}
        primaryActionText="Delete"
        secondaryActionText="Cancel"
        loading={deleteDialog?.loading}
        onPrimaryAction={async () => {
          if (!deleteDialog) return;
          setDeleteDialog((d) => d && { ...d, loading: true });
          try {
            await deleteCustomReport(deleteDialog.report.id);
            setCustomReports((prev) =>
              prev.filter((r) => r.id !== deleteDialog.report.id)
            );
            setDeleteDialog(null);
          } catch {
            setDeleteDialog((d) => d && { ...d, loading: false });
          }
        }}
        onSecondaryAction={() => setDeleteDialog(null)}
      />
    </VStack>
  );
};

export default Reports;
